import React from 'react';
import styled from 'styled-components';
import useOperatorContext from '../../../../contexts/operator/useOperatorContext';
import useSiteContext from '../../../../contexts/site/useSiteContext';
import { money } from '../../../../services/format';
import { isOfferOnMenu, offerDetails, offerHeadline, offerNames } from '../../../../services/offers';

/**
 * What customers can have without a code, at the top of the menu: a card for each offer, with its small print.
 */
const OfferCards = () => {
    const operatorContext = useOperatorContext();
    const siteContext = useSiteContext();

    const format = (pence: number) => money(pence, operatorContext.operator?.ccy, operatorContext.operator?.locale);
    const offers = siteContext.offers.filter((offer) => isOfferOnMenu(offer, siteContext.menu));

    if (offers.length === 0) {
        return null;
    }

    return (
        <StyledOfferCards className="offer-cards">
            {offers.map((offer) => {
                const details = offerDetails(offer, format);

                return (
                    <div key={offer.uid} className="offer-card">
                        <div className="offer-card__details">
                            <h3 className="offer-card__title">{offerHeadline(offer, offerNames(offer, siteContext.menu), format)}</h3>
                            {details.length > 0 && <p className="offer-card__text">{details.join(' · ')}</p>}
                        </div>
                        <span className="material-symbols-outlined material-symbols-outlined--filled offer-card__icon">sell</span>
                    </div>
                );
            })}
        </StyledOfferCards>
    );
};

/**
 * The cards sit in the same grid as the item cards (same gaps, same side padding, same border), so the two line up.
 * The menu's top spacing (24px under the navigation) is also what is left below the cards before the first category.
 */
const StyledOfferCards = styled.div`
    display: grid;
    justify-content: start;
    margin-top: 24px;
    grid-template-columns: repeat(1, minmax(0, 1fr));

    ${(props) => props.theme.v4.card_style === 'portrait' ? `
        gap: 16px;
        padding: 0 16px;

        @media screen and (min-width: 768px) {
            grid-template-columns: repeat(auto-fill, minmax(282px, 1fr));
            gap: 24px;
            padding: 0 48px;
        }
    ` : `
        /* (item cards are a plain list on mobile: edge to edge, sharing their borders) */
        @media screen and (min-width: 768px) {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 24px;
            padding: 0 48px;
        }

        /* (wide enough for three cards, about 330px each, without their text wrapping) */
        @media screen and (min-width: 1140px) {
            grid-template-columns: repeat(3, minmax(0, 1fr));
        }
    `}

    .offer-card {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 16px;
        box-sizing: border-box;
        background: var(--color_ui_7);
        border: var(--ui_card_border);
        border-radius: var(--radius_xl);

        ${(props) => props.theme.v4.card_style === 'portrait' ? '' : `
            @media screen and (max-width: 767px) {
                border-radius: 0;

                &:not(:first-child) {
                    border-top-width: 0;
                }
            }
        `}
    }

    .offer-card__details {
        flex: 1;
        min-width: 0;
    }

    .offer-card__title {
        margin-bottom: 0;
        font-size: var(--core_text_ui_lg);
        line-height: 20px;
        font-weight: 400;
        color: var(--text-black);

        @media screen and (min-width: 576px) {
            font-size: var(--core_text_ui_lg_desktop);
        }
    }

    .offer-card__text {
        margin: 4px 0 0;
        font-size: 14px;
        line-height: 20px;
        color: var(--text-grey);
    }

    .offer-card__icon {
        flex-shrink: 0;
        font-size: 28px;
        color: var(--ui_primary_cta);
    }
`;

export default OfferCards;
